import { useLocale as useLocaleR42 } from '@/lib/locale';
import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Eye, MessageSquare, ShieldCheck } from 'lucide-react';
import { PortraitVideoCard } from './PortraitVideoCard';
import { WaveMascot } from './WaveMascot';

const reveal = { initial: { opacity: 0, y: 14 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.25 }, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } };

const steps = [
    { label: 'Ask', caption: 'Say what you need', icon: MessageSquare },
    { label: 'Preview', caption: 'See exactly what changes', icon: Eye },
    { label: 'Confirm', caption: 'One tap to apply', icon: CheckCircle2 },
];

export const SplashSISection = () => {
    const { t: t42 } = useLocaleR42();
    const reduced = useReducedMotion();
    const entrance = reduced ? { initial: false } : reveal;
    return (
    <section id="meet-si" className="relative scroll-mt-24 overflow-hidden bg-white py-10 lg:py-24" aria-labelledby="landing-si-heading" data-testid="landing-si-section">
        <div aria-hidden="true" className="pointer-events-none absolute -right-40 top-10 h-[480px] w-[480px] rounded-full bg-[var(--sp-cyan)]/10 blur-3xl" />
        {/* DOM order = mobile order: header → phone video → details. On lg the grid
            re-places them: header (row 1) + details (row 2) in the left 7 cols,
            video spans both rows in the right 5 cols. */}
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 sm:px-6 lg:grid-cols-12 lg:grid-rows-[auto_auto] lg:gap-x-10 lg:gap-y-8 lg:px-8">
            <motion.div {...entrance} className="min-w-0 lg:col-span-7 lg:col-start-1 lg:row-start-1 lg:self-end" data-testid="landing-si-header">
                <p className="sp-eyebrow" data-testid="landing-si-eyebrow">{t42("Super Intelligence · in every coach account")}</p>

                {/* Headline + mascot side by side (owner request) */}
                <div className="sp-si-headline-row mt-3">
                    <h2 id="landing-si-heading" className="sp-si-heading font-display font-extrabold text-[color:var(--sp-text)]" data-testid="landing-si-heading"> {t42("Meet Splash,")}<br />{t42("your")} <span className="text-[color:var(--sp-ocean)]">{t42("SI agent.")}</span>
                    </h2>
                    <div className="sp-wave-companion" data-testid="landing-si-mascot">
                        <WaveMascot loop testId="landing-si-wave" />
                    </div>
                </div>

                <p className="sp-sub mt-5 max-w-xl" data-testid="landing-si-intro"> {t42("Describe a task. Splash previews it. You confirm. Less admin, more coaching — with an AI-powered assistant that lives inside SplashPad.")} </p>
            </motion.div>

            {/* Phone video — second in DOM so phones see it right after the headline */}
            <motion.div {...entrance} transition={{ ...reveal.transition, delay: 0.1 }} className="min-w-0 lg:col-span-5 lg:col-start-8 lg:row-span-2 lg:row-start-1 lg:self-center" data-testid="landing-si-video-column">
                <PortraitVideoCard testId="landing-si-commercial" />
            </motion.div>

            <motion.div {...entrance} className="min-w-0 lg:col-span-7 lg:col-start-1 lg:row-start-2" data-testid="landing-si-details">
                {/* Ask → Preview → Confirm */}
                <ol className="relative grid max-w-md grid-cols-3 gap-2" data-testid="landing-si-action-steps">
                    <div aria-hidden="true" className="pointer-events-none absolute left-[17%] right-[17%] top-6 border-t-2 border-dashed border-[color:var(--sp-border-strong)]" />
                    {steps.map(({ label, caption, icon: Icon }, i) => (
                        <li key={label} className="relative flex flex-col items-center text-center" data-testid={`landing-si-step-${i + 1}`}>
                            <span className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--sp-navy)] text-[var(--sp-cyan)] shadow-[0_10px_22px_rgba(10,22,40,0.28)] ring-4 ring-white">
                                <Icon className="h-5 w-5" aria-hidden="true" />
                                <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--sp-gold)] text-[10px] font-extrabold text-[color:var(--sp-navy)] ring-2 ring-white">{i + 1}</span>
                            </span>
                            <span className="mt-2.5 font-display text-sm font-bold text-[color:var(--sp-text)]">{label}</span>
                            <span className="mt-0.5 text-xs leading-snug text-[color:var(--sp-text-subtle)]">{caption}</span>
                        </li>
                    ))}
                </ol>

                <p className="mt-7 flex items-start gap-2 text-sm text-[color:var(--sp-text-muted)]" data-testid="landing-si-trust-copy">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--sp-ocean)]" aria-hidden="true" /> {t42("Nothing changes in your account until you confirm.")} </p>

                <div className="mt-7 flex flex-wrap items-center gap-3">
                    <Link to="/signup?role=instructor" className="sp-btn-primary" data-testid="landing-si-primary-cta">{t42("Start free trial")} <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
                    <a href="#product-tour" className="sp-btn-secondary" data-testid="landing-si-pricing-link">{t42("See the product")}</a>
                </div>
            </motion.div>
        </div>
    </section>
    );
};

export default SplashSISection;
